import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { hasCmeCxRecordCapability, hasCmeCxScoreCapability } from '../../Common/Utils/CmeCapabilityUtils'
import { hasBusinessIntegrationOption } from '../../Common/Utils/CxProductApplicability'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { CapabilityBitFlags } from '../../CMEAddModify/Constants'
import { BUSINESS_OPTION, BusinessOption } from '../Constants/Constants'

/**
 * Business options that need a per-TelSys-type CME capability (APD-13052).
 */
export type CmeGatedBusinessOption = typeof BUSINESS_OPTION.CX_SCORE | typeof BUSINESS_OPTION.CX_RECORD

/**
 * Whether a CME-gated business option (CX Score / CX Record) can be offered on the TelSys.
 *
 * It is offered when the CME has the matching capability for the TelSys type, or when the saved TelSys already
 * has the option (the backend accepts an unchanged option). While the CME capabilities are unknown, nothing is
 * hidden; the backend still enforces the rule on save.
 *
 * @param option The business option.
 * @param telSysType The TelSys type selected in the form.
 * @param cmeCapabilities The owning CME's {@code Capabilities} bitmask, or undefined while not loaded.
 * @param savedBusinessOptions The business options of the saved TelSys, when editing one.
 * @returns True when the option can be offered.
 */
export const isCmeGatedBusinessOptionAllowed = (
    option: CmeGatedBusinessOption,
    telSysType: Nullable<TelSysTypes>,
    cmeCapabilities: Definable<number>,
    savedBusinessOptions: Definable<readonly BusinessOption[]>
): boolean => {
    if (hasBusinessIntegrationOption(savedBusinessOptions, option)) return true

    return option === BUSINESS_OPTION.CX_SCORE
        ? hasCmeCxScoreCapability(cmeCapabilities, telSysType)
        : hasCmeCxRecordCapability(cmeCapabilities, telSysType)
}

/**
 * Removes the CME-gated business options (CX Score / CX Record) that are not allowed from a selection.
 *
 * @param options The selected business options.
 * @param isCxScoreAllowed Whether CX Score can be offered (see {@link isCmeGatedBusinessOptionAllowed}).
 * @param isCxRecordAllowed Whether CX Record can be offered.
 * @returns The options that remain allowed.
 */
export const withoutDisallowedCmeGatedOptions = (
    options: readonly BusinessOption[],
    isCxScoreAllowed: boolean,
    isCxRecordAllowed: boolean
): BusinessOption[] =>
    options.filter(
        (option: BusinessOption): boolean =>
            (option !== BUSINESS_OPTION.CX_SCORE || isCxScoreAllowed) &&
            (option !== BUSINESS_OPTION.CX_RECORD || isCxRecordAllowed)
    )

/**
 * Whether the CX Transcribe business option can be offered (APD-14047). Business options only exist on MS Teams,
 * so it needs the CME's CX Transcribe capability for Teams, unless the saved TelSys already has the option (the
 * backend accepts an unchanged option). While the CME capabilities are unknown, nothing is hidden.
 *
 * @param cmeCapabilities The owning CME's {@code Capabilities} bitmask, or undefined while not loaded.
 * @param savedBusinessOptions The business options of the saved TelSys, when editing one.
 * @returns True when the option can be offered.
 */
export const isTranscribeOptionAllowed = (
    cmeCapabilities: Definable<number>,
    savedBusinessOptions: Definable<readonly BusinessOption[]>
): boolean =>
    hasBusinessIntegrationOption(savedBusinessOptions, BUSINESS_OPTION.CX_TRANSCRIBE) ||
    // Only "not loaded" counts as unknown: a loaded CME with no capabilities (0) does not get the option.
    IsVarEmpty(cmeCapabilities) ||
    isBitmaskMatching(cmeCapabilities, CapabilityBitFlags.CXT_TEAMS)

/**
 * Whether the selected business options let CX Transcribe be selected: it needs CX Score or CX Record.
 *
 * @param selectedOptions The business options currently selected.
 * @returns True when CX Score or CX Record is selected.
 */
export const isTranscribeOptionQualified = (selectedOptions: Nullable<readonly BusinessOption[]>): boolean =>
    !!selectedOptions?.some(
        (option: BusinessOption): boolean => option === BUSINESS_OPTION.CX_SCORE || option === BUSINESS_OPTION.CX_RECORD
    )

/**
 * Removes CX Transcribe from a selection when it is not allowed (see {@link isTranscribeOptionAllowed}) or no
 * longer qualified (see {@link isTranscribeOptionQualified}).
 *
 * @param options The selected business options.
 * @param isAllowed Whether CX Transcribe is allowed.
 * @returns The options that remain valid.
 */
export const withoutInvalidTranscribeOption = (
    options: readonly BusinessOption[],
    isAllowed: boolean
): BusinessOption[] =>
    isAllowed && isTranscribeOptionQualified(options)
        ? [...options]
        : options.filter((option: BusinessOption): boolean => option !== BUSINESS_OPTION.CX_TRANSCRIBE)

/**
 * Whether CX Transcribe goes in the business option list. It is offered when the feature flag is on, the CME allows
 * it and CX Score or CX Record is selected. An option that is already selected stays listed whatever the flag or the
 * rules say, so the user can see it and untick it (as with a saved CX Score / CX Record).
 *
 * @param isFeatureEnabled Whether the {@code enableTelSysTranscriptionOption} feature flag is on.
 * @param isAllowed Whether the CME allows it (see {@link isTranscribeOptionAllowed}).
 * @param isQualified Whether CX Score or CX Record is selected (see {@link isTranscribeOptionQualified}).
 * @param selectedOptions The business options currently selected.
 * @returns True when the option should be listed.
 */
export const shouldListTranscribeOption = (
    isFeatureEnabled: boolean,
    isAllowed: boolean,
    isQualified: boolean,
    selectedOptions: Nullable<readonly BusinessOption[]>
): boolean =>
    (isFeatureEnabled && isAllowed && isQualified) || !!selectedOptions?.includes(BUSINESS_OPTION.CX_TRANSCRIBE)
